import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HomeIcon from '@mui/icons-material/Home';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { branchService } from '@/data/branchService';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  EXTERNAL_ORDER_STATUS_COLOR,
  EXTERNAL_ORDER_STATUS_LABELS,
  EXTERNAL_WEBHOOK_STATUS_COLOR,
  EXTERNAL_WEBHOOK_STATUS_LABELS,
  ExternalOrderRow,
  ExternalOrderStatus,
  ExternalWebhookLog,
  ExternalWebhookStatus,
  FOOD_PROVIDER_LABELS,
  FoodProviderCode
} from '@/types/FoodDelivery.interface';
import FoodDeliveryEmptyState from '../food-delivery/components/FoodDeliveryEmptyState';
import { serverMessage, headerBranchId } from '../food-delivery/helpers';
import { Branch } from '@/types/Branch.interface';
import OrderDetailDialog from './components/OrderDetailDialog';

type Toast = { msg: string; severity: 'success' | 'error' | 'info' } | null;

const fmt = (v: string | null) =>
  v ? new Date(v).toLocaleString('tr-TR') : '—';

// Trendyol GO restoran-kaynaklı iptal nedenleri (resmi doküman "Paket Modelleri";
// 624/626 yalnız kendi kuryeli Model 1 satıcılar için — listelenmez).
const TGO_REJECT_REASONS: { code: string; label: string }[] = [
  { code: '621', label: 'Tedarik problemi' },
  { code: '622', label: 'Mağaza kapalı' },
  { code: '623', label: 'Mağaza siparişi hazırlayamıyor' },
  { code: '627', label: 'Sipariş karışıklığı' }
];

const FoodDeliveryOrders: React.FC = () => {
  const { isBranchAdmin, isAdminView, currentBranch } = useUserViewMode();

  const [tab, setTab] = useState(0);
  const [branches, setBranches] = useState<Branch[]>([]);
  const headerBranch = headerBranchId(currentBranch, isAdminView);
  const [branchId, setBranchId] = useState<number | null>(headerBranch);
  // Üst menüden şube değişince sayfa da o şubeye geçer.
  useEffect(() => {
    if (headerBranch) setBranchId(headerBranch);
  }, [headerBranch]);
  const [toast, setToast] = useState<Toast>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Siparişler
  const [rows, setRows] = useState<ExternalOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<ExternalOrderStatus | ''>(
    ''
  );
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  // Özel onay modalları (window.confirm/prompt KULLANILMAZ)
  const [rejectTarget, setRejectTarget] = useState<ExternalOrderRow | null>(
    null
  );
  const [rejectReason, setRejectReason] = useState('');
  const [cancelAckTarget, setCancelAckTarget] =
    useState<ExternalOrderRow | null>(null);

  // Webhook / dead-letter
  const [logs, setLogs] = useState<ExternalWebhookLog[]>([]);
  const [logStatus, setLogStatus] = useState<ExternalWebhookStatus | ''>('');
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (isBranchAdmin) return;
    branchService
      .getAllFlat({ getAll: true })
      .then(setBranches)
      .catch(() =>
        setToast({ msg: 'Şubeler getirilemedi.', severity: 'error' })
      );
  }, [isBranchAdmin]);

  const loadOrders = useCallback(async () => {
    if (!branchId) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const res = await foodDeliveryService.listOrders({
        branchId,
        status: statusFilter || undefined,
        page: page + 1,
        pageSize
      });
      setRows(res.data);
      setTotal(res.total);
    } catch {
      setToast({ msg: 'Siparişler getirilemedi.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [branchId, statusFilter, page, pageSize]);

  const loadLogs = useCallback(async () => {
    if (!branchId) {
      setLogs([]);
      return;
    }
    setLogsLoading(true);
    try {
      setLogs(
        await foodDeliveryService.listWebhookLogs({
          branchId,
          status: logStatus || undefined
        })
      );
    } catch {
      setToast({ msg: 'Webhook logları getirilemedi.', severity: 'error' });
    } finally {
      setLogsLoading(false);
    }
  }, [branchId, logStatus]);

  useEffect(() => {
    if (tab === 0) loadOrders();
  }, [tab, loadOrders]);
  useEffect(() => {
    if (tab === 1) loadLogs();
  }, [tab, loadLogs]);

  const handleReprocess = async (row: ExternalOrderRow) => {
    setBusyId(row.id);
    try {
      const res = await foodDeliveryService.reprocessOrder(row.id);
      setToast({
        msg: res.requeued
          ? 'Sipariş yeniden işleme alındı.'
          : 'Yeniden işlenecek webhook bulunamadı.',
        severity: res.requeued ? 'success' : 'info'
      });
      await loadOrders();
    } catch {
      setToast({ msg: 'Yeniden işleme başarısız.', severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleAccept = async (row: ExternalOrderRow) => {
    setBusyId(row.id);
    try {
      const res = await foodDeliveryService.acceptOrder(row.id);
      if (res.outcome === 'CONVERTED') {
        setToast({
          msg: `Sipariş kabul edildi → mutfak siparişi #${res.internalOrderId}.`,
          severity: 'success'
        });
      } else if (res.outcome === 'MANUAL_REVIEW') {
        setToast({
          msg: 'Kabul edildi ancak eşleşmeyen ürün var — manuel incelemeye düştü.',
          severity: 'info'
        });
      } else {
        setToast({ msg: `Sonuç: ${res.outcome}`, severity: 'info' });
      }
      await loadOrders();
    } catch {
      setToast({ msg: 'Sipariş kabul edilemedi.', severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = (row: ExternalOrderRow) => {
    setRejectReason(
      row.providerCode === FoodProviderCode.TRENDYOL_GO
        ? '623'
        : 'REJECTED_BY_STAFF'
    );
    setRejectTarget(row);
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    const row = rejectTarget;
    setRejectTarget(null);
    setBusyId(row.id);
    try {
      await foodDeliveryService.rejectOrder(
        row.id,
        rejectReason || 'UNSPECIFIED'
      );
      setToast({ msg: 'Sipariş reddedildi.', severity: 'success' });
      await loadOrders();
    } catch (e) {
      setToast({
        msg: serverMessage(e, 'Sipariş reddedilemedi.'),
        severity: 'error'
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleReady = async (row: ExternalOrderRow) => {
    setBusyId(row.id);
    try {
      await foodDeliveryService.markOrderReady(row.id);
      setToast({
        msg: 'Sipariş hazır olarak işaretlendi.',
        severity: 'success'
      });
      await loadOrders();
    } catch (e) {
      setToast({
        msg: serverMessage(e, 'Hazır bildirimi başarısız.'),
        severity: 'error'
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDispatched = async (row: ExternalOrderRow) => {
    setBusyId(row.id);
    try {
      await foodDeliveryService.markOrderDispatched(row.id);
      setToast({ msg: 'Sipariş yola çıktı.', severity: 'success' });
      await loadOrders();
    } catch (e) {
      setToast({
        msg: serverMessage(e, 'Yola çıktı bildirimi başarısız.'),
        severity: 'error'
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelivered = async (row: ExternalOrderRow) => {
    setBusyId(row.id);
    try {
      await foodDeliveryService.markOrderDelivered(row.id);
      setToast({ msg: 'Sipariş teslim edildi.', severity: 'success' });
      await loadOrders();
    } catch (e) {
      setToast({
        msg: serverMessage(e, 'Teslim bildirimi başarısız.'),
        severity: 'error'
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleCancelAck = (row: ExternalOrderRow) => {
    setCancelAckTarget(row);
  };

  const confirmCancelAck = async () => {
    if (!cancelAckTarget) return;
    const row = cancelAckTarget;
    setCancelAckTarget(null);
    setBusyId(row.id);
    try {
      await foodDeliveryService.cancelAckOrder(row.id);
      setToast({
        msg: 'Platform iptali onaylandı — sipariş kapatıldı.',
        severity: 'success'
      });
      await loadOrders();
    } catch {
      setToast({ msg: 'İptal onayı başarısız.', severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleRetryLog = async (log: ExternalWebhookLog) => {
    setBusyId(log.id);
    try {
      const res = await foodDeliveryService.retryWebhookLog(log.id);
      setToast({
        msg: res.requeued
          ? 'Webhook yeniden kuyruğa eklendi.'
          : 'Kuyruğa eklenemedi.',
        severity: res.requeued ? 'success' : 'info'
      });
      await loadLogs();
    } catch {
      setToast({ msg: 'Retry başarısız.', severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const branchSelector = !isBranchAdmin && (
    <Autocomplete
      sx={{ maxWidth: 420, mb: 3 }}
      options={branches}
      getOptionLabel={(b) => b.name}
      value={branches.find((b) => b.id === branchId) ?? null}
      onChange={(_e, val) => {
        setBranchId(val?.id ?? null);
        setPage(0);
      }}
      renderInput={(params) => <TextField {...params} label="Şube seç" />}
    />
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitleWrapper>
        <Box>
          <Typography variant="h3" component="h3" gutterBottom>
            Yemek Platformu Siparişleri
          </Typography>
          <Typography variant="subtitle2">
            Gelen external siparişleri, manuel inceleme kuyruğunu ve webhook
            günlüklerini izleyin.
          </Typography>
        </Box>
      </PageTitleWrapper>

      <Box sx={{ px: 4, pb: 4 }}>
        <Card>
          <CardContent>
            {branchSelector}

            <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Siparişler" />
              <Tab label="Webhook / Dead-letter" />
            </Tabs>

            {!branchId ? (
              <Alert severity="info">
                Verileri görüntülemek için bir şube seçin.
              </Alert>
            ) : tab === 0 ? (
              <>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    select
                    size="small"
                    label="Statü"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(
                        e.target.value as ExternalOrderStatus | ''
                      );
                      setPage(0);
                    }}
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    {Object.values(ExternalOrderStatus).map((s) => (
                      <MenuItem key={s} value={s}>
                        {EXTERNAL_ORDER_STATUS_LABELS[s]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title="Yenile">
                    <IconButton onClick={loadOrders}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {loading ? (
                  <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                  </Box>
                ) : rows.length === 0 ? (
                  <FoodDeliveryEmptyState
                    message="Bu kriterlerde sipariş yok. Trendyol GO bildirim (webhook) göndermez; siparişler ~30 saniyede bir yoklamayla alınır. Sipariş hiç düşmüyorsa entegrasyonun durumunu ve son hata mesajını kontrol edin."
                    actionLabel="Entegrasyon durumunu aç"
                  />
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Platform</TableCell>
                          <TableCell>Dış No</TableCell>
                          <TableCell>Müşteri</TableCell>
                          <TableCell>Statü</TableCell>
                          <TableCell>Mutfak No</TableCell>
                          <TableCell align="right">Kalem</TableCell>
                          <TableCell align="right">Tutar</TableCell>
                          <TableCell>Alındı</TableCell>
                          <TableCell align="right">İşlemler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>
                              {FOOD_PROVIDER_LABELS[row.providerCode]}
                            </TableCell>
                            <TableCell>
                              {row.orderNumber ?? row.externalOrderId}
                            </TableCell>
                            <TableCell>{row.customerName ?? '—'}</TableCell>
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Chip
                                  size="small"
                                  label={
                                    EXTERNAL_ORDER_STATUS_LABELS[row.status]
                                  }
                                  color={
                                    EXTERNAL_ORDER_STATUS_COLOR[row.status]
                                  }
                                />
                                {/* Operatörün listeden görmesi gerekenler:
                                    kurye tahsil edecek mi, kurye çıkacak mı,
                                    sipariş ciroya girecek mi. */}
                                {row.paymentType === 'PAY_WITH_ON_DELIVERY' && (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label="Kapıda"
                                  />
                                )}
                                {row.isPickup && (
                                  <Chip
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    label="Gel-Al"
                                  />
                                )}
                                {row.isTestOrder && (
                                  <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label="Test"
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {row.internalOrderId ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  label={`#${row.internalOrderId}`}
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell align="right">{row.itemCount}</TableCell>
                            <TableCell align="right">
                              {row.totalAmount
                                ? `${row.totalAmount} ${row.currency ?? ''}`
                                : '—'}
                            </TableCell>
                            <TableCell>{fmt(row.receivedAt)}</TableCell>
                            <TableCell align="right">
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="flex-end"
                              >
                                {busyId === row.id ? (
                                  <CircularProgress size={20} sx={{ m: 1 }} />
                                ) : (
                                  <>
                                    <Tooltip title="Detay">
                                      <IconButton
                                        size="small"
                                        onClick={() => setDetailId(row.id)}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {(row.status ===
                                      ExternalOrderStatus.RECEIVED ||
                                      row.status ===
                                        ExternalOrderStatus.PENDING_ACCEPTANCE) &&
                                      row.internalOrderId == null && (
                                        <>
                                          <Tooltip title="Kabul et (mutfağa düşür)">
                                            <IconButton
                                              size="small"
                                              color="success"
                                              onClick={() => handleAccept(row)}
                                            >
                                              <CheckCircleIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Reddet">
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => handleReject(row)}
                                            >
                                              <CancelIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                    {row.internalOrderId != null &&
                                      row.status ===
                                        ExternalOrderStatus.PREPARING && (
                                        <Tooltip title="Hazır bildir">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleReady(row)}
                                          >
                                            <DoneAllIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    {/* Yola çıktı / teslim edildi yalnız kendi
                                        kuryesiyle çalışan (Model 1) mağazalarda
                                        kullanılabilir; platform kuryesinde bu
                                        aşamaları Trendyol yürütür. */}
                                    {row.internalOrderId != null &&
                                      row.deliveryModel === 'STORE' &&
                                      row.status ===
                                        ExternalOrderStatus.READY && (
                                        <Tooltip title="Yola çıktı bildir">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() =>
                                              handleDispatched(row)
                                            }
                                          >
                                            <LocalShippingIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    {row.internalOrderId != null &&
                                      row.deliveryModel === 'STORE' &&
                                      (row.status ===
                                        ExternalOrderStatus.DISPATCHED ||
                                        row.status ===
                                          ExternalOrderStatus.READY) && (
                                        <Tooltip title="Teslim edildi bildir">
                                          <IconButton
                                            size="small"
                                            color="success"
                                            onClick={() => handleDelivered(row)}
                                          >
                                            <HomeIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    {(row.status ===
                                      ExternalOrderStatus.CANCELED ||
                                      row.status ===
                                        ExternalOrderStatus.REFUNDED) &&
                                      (row.cancelAcked ? (
                                        <Tooltip title="Platform iptali operatör tarafından onaylandı">
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            color="success"
                                            icon={
                                              <AssignmentTurnedInIcon fontSize="small" />
                                            }
                                            label="İptal Onaylandı"
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title="Platform iptalini onayla">
                                          <IconButton
                                            size="small"
                                            color="warning"
                                            onClick={() => handleCancelAck(row)}
                                          >
                                            <AssignmentTurnedInIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      ))}
                                    <Tooltip title="Yeniden işle">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleReprocess(row)}
                                      >
                                        <ReplayIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={total}
                      page={page}
                      onPageChange={(_e, p) => setPage(p)}
                      rowsPerPage={pageSize}
                      onRowsPerPageChange={(e) => {
                        setPageSize(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[10, 20, 50, 100]}
                    />
                  </TableContainer>
                )}
              </>
            ) : (
              <>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    select
                    size="small"
                    label="Statü"
                    value={logStatus}
                    onChange={(e) =>
                      setLogStatus(e.target.value as ExternalWebhookStatus | '')
                    }
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    {Object.values(ExternalWebhookStatus).map((s) => (
                      <MenuItem key={s} value={s}>
                        {EXTERNAL_WEBHOOK_STATUS_LABELS[s]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title="Yenile">
                    <IconButton onClick={loadLogs}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {logsLoading ? (
                  <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                  </Box>
                ) : logs.length === 0 ? (
                  <Alert severity="info">Webhook logu yok.</Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Platform</TableCell>
                          <TableCell>Event ID</TableCell>
                          <TableCell>Tip</TableCell>
                          <TableCell>Statü</TableCell>
                          <TableCell>Hata</TableCell>
                          <TableCell>Alındı</TableCell>
                          <TableCell align="right">İşlemler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logs.map((log) => (
                          <TableRow key={log.id} hover>
                            <TableCell>
                              {FOOD_PROVIDER_LABELS[log.providerCode]}
                            </TableCell>
                            <TableCell>{log.eventId ?? '—'}</TableCell>
                            <TableCell>{log.eventType ?? '—'}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  EXTERNAL_WEBHOOK_STATUS_LABELS[log.status]
                                }
                                color={
                                  EXTERNAL_WEBHOOK_STATUS_COLOR[log.status]
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{
                                  display: 'block',
                                  maxWidth: 260,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                                title={log.errorMessage ?? ''}
                              >
                                {log.errorMessage ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>{fmt(log.receivedAt)}</TableCell>
                            <TableCell align="right">
                              {busyId === log.id ? (
                                <CircularProgress size={20} sx={{ m: 1 }} />
                              ) : (
                                <Tooltip title="Yeniden kuyruğa al">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRetryLog(log)}
                                  >
                                    <ReplayIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <OrderDetailDialog
        orderId={detailId}
        open={detailId != null}
        onClose={() => setDetailId(null)}
      />

      {/* Reddetme modalı — sebep seçimi (TGO: resmi neden kodları) */}
      <Dialog
        open={rejectTarget != null}
        onClose={() => setRejectTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Siparişi Reddet</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Sipariş platforma iade edilecek ve müşteriye iptal olarak
            yansıyacak. Bu işlem geri alınamaz.
          </DialogContentText>
          {rejectTarget?.providerCode === FoodProviderCode.TRENDYOL_GO ? (
            <TextField
              select
              fullWidth
              label="Red nedeni"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            >
              {TGO_REJECT_REASONS.map((r) => (
                <MenuItem key={r.code} value={r.code}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              fullWidth
              label="Red nedeni kodu"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              helperText="Platformun beklediği neden kodu (ör. OUT_OF_STOCK)"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Vazgeç</Button>
          <Button color="error" variant="contained" onClick={confirmReject}>
            Reddet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Platform iptali onay modalı */}
      <Dialog
        open={cancelAckTarget != null}
        onClose={() => setCancelAckTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Platform İptalini Onayla</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Müşteri/platform bu siparişi iptal etti. Onayladığınızda mutfak
            siparişi iptal edilir ve düşülen stok geri yüklenir. (Sipariş zaten
            iptal edildiyse yalnızca &quot;görüldü&quot; kaydı düşülür.)
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelAckTarget(null)}>Vazgeç</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={confirmCancelAck}
          >
            İptali Onayla
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FoodDeliveryOrders;
